import React, {
    FC,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import type { IHeaderParams, SortDirection } from 'ag-grid-community';

import styled from 'styled-components';

//constants
import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

//components
import AampeTooltip from '../shared/AampeTooltip/AampeTooltip';

interface AGCustomHeaderProps {
    props: IHeaderParams;
    toolTipTitle?: string;
    children?: ReactNode;
}

const AGCustomHeaderComponent: FC<AGCustomHeaderProps> = ({
    props,
    toolTipTitle,
    children,
}) => {
    const { setSort, column, displayName, showColumnMenu } = props;
    const colDef = useMemo(() => column.getColDef(), [column]);
    const enableMenu = colDef.headerComponentParams?.enableMenu === true;
    const [sort, setSortState] = useState<SortDirection | undefined>(
        column.getSort() || undefined,
    );

    const menuButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onSortChanged = () => {
            setSortState(column.getSort() || undefined);
        };

        column.addEventListener('sortChanged', onSortChanged);

        return () => {
            column.removeEventListener('sortChanged', onSortChanged);
        };
    }, [column]);

    const sortHandler = useCallback(
        (event: React.MouseEvent) => {
            // Prevent event bubbling to avoid triggering menu
            event.stopPropagation();

            if (sort === 'desc') {
                setSort(null);
            } else if (sort === undefined || null) {
                setSort('asc');
            } else {
                setSort('desc');
            }
        },
        [sort, setSort],
    );

    const menuHandler = useCallback(() => {
        if (showColumnMenu && menuButtonRef.current) {
            showColumnMenu(menuButtonRef.current);
        }
    }, [showColumnMenu]);

    const isDescending = useMemo(() => sort === 'desc', [sort]);
    const isAscending = useMemo(() => sort === 'asc', [sort]);

    return (
        <HeaderContainer>
            <AampeTooltip title={toolTipTitle}>
                <SortContainer onClick={sortHandler} enableMenu={enableMenu}>
                    <span>{children ? children : displayName}</span>
                    {isDescending ? (
                        <StyledDownIcon />
                    ) : isAscending ? (
                        <StyledUpIcon />
                    ) : (
                        <></>
                    )}
                </SortContainer>
            </AampeTooltip>

            {/* Menu button - only show if menu is enabled */}
            {enableMenu && (
                <MenuButton
                    ref={menuButtonRef}
                    onClick={menuHandler}
                    className="ag-header-cell-menu-button"
                >
                    <span
                        className="ag-icon ag-icon-menu"
                        role="presentation"
                    />
                </MenuButton>
            )}
        </HeaderContainer>
    );
};

const AGCustomHeader = React.memo(AGCustomHeaderComponent);
export default AGCustomHeader;

const HeaderContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden', // Prevent content from spilling out
});

const SortContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'enableMenu',
})<{ enableMenu: boolean }>(({ enableMenu }) => ({
    display: 'flex',
    gap: aampeTheme.spacing4(1),
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    paddingRight: enableMenu ? aampeTheme.spacing4(5) : 0,
    flex: 1, // Take available space but respect container bounds
}));

const MenuButton = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: aampeTheme.spacing4(0.5),
    opacity: 0.7,
    transition: 'opacity 0.2s',
    // Position absolutely but with more space from the right edge
    position: 'absolute',
    right: aampeTheme.spacing4(1),
    top: '50%',
    transform: 'translateY(-50%)',

    '&:hover': {
        opacity: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
    },
});

const StyledUpIcon = styled(ICONS.upArrow)({
    height: aampeTheme.spacing4(3),
    width: aampeTheme.spacing4(3),
});

const StyledDownIcon = styled(ICONS.downArrow)({
    height: aampeTheme.spacing4(3),
    width: aampeTheme.spacing4(3),
});
